import { useRef, useState, useEffect } from 'react'
import { Camera, CheckCircle, AlertCircle, Loader } from 'lucide-react'
import type { FacilityArea } from '../../lib/services/survey'
import { surveyStore, type PendingPhoto } from '../../lib/offline/surveyStore'
import { dataUrlToBlob, encodeBlobForStorage } from '../../lib/offline/photoPreview'
import { enqueuePhoto, uploadPendingPhotoEvidence } from '../../lib/services/surveySync'
import { assertDecodableImage } from '../../lib/media/validateImage'

interface PhotoCaptureProps {
  visitId: string
  surveyId: string
  area: FacilityArea
  onCapture?: (photo: PendingPhoto) => void
  /**
   * Visual treatment for the capture trigger.
   *   - `pill` (default, legacy): solid brand-color button.
   *   - `dashed-tile`: full-width dashed-border tile matching the
   *     DailyAssessment pattern so daily + guided survey look identical.
   *   - `secondary`: compact outlined button intended for "Add another"
   *     next to an existing thumbnail grid.
   */
  variant?: 'pill' | 'dashed-tile' | 'secondary'
  /** Override the button label (e.g., "Take Photo" / "Add another"). */
  label?: string
  /** Upload and commit evidence before exposing the photo id to callers. */
  requireDurableUpload?: boolean
}

/** Read GPS from the browser's Geolocation API (best-effort). */
function getPosition(): Promise<{ lat: number; lng: number } | null> {
  if (!navigator.geolocation) return Promise.resolve(null)
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: false, timeout: 3000 },
    )
  })
}

export function PhotoCapture({ visitId, surveyId, area, onCapture, variant = 'pill', label, requireDurableUpload = false }: PhotoCaptureProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [status, setStatus] = useState<'idle' | 'processing' | 'saved' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Use a native DOM event listener instead of React's onChange.
  // React's synthetic change event can silently fail on iOS WebKit
  // when the file picker returns from background.
  const propsRef = useRef({ visitId, surveyId, area, onCapture, requireDurableUpload })
  useEffect(() => { propsRef.current = { visitId, surveyId, area, onCapture, requireDurableUpload } })

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const handleChange = async () => {
      const file = input.files?.[0]
      if (!file) {
        setStatus('error')
        setErrorMsg('No photo received — please try again')
        return
      }

      setStatus('processing')
      setErrorMsg('Reading photo…')

      try {
        if (file.size < 100) {
          setStatus('error')
          setErrorMsg('Photo appears empty or corrupted — please retake')
          input.value = ''
          return
        }

        const blob: Blob = file
        setErrorMsg('Validating photo…')
        await assertDecodableImage(blob)
        const { visitId: vid, surveyId: sid, area: a, onCapture: cb, requireDurableUpload: mustUploadFirst } = propsRef.current

        // Materialize File → in-memory data URL + Blob on the same task that
        // received the picker result (HCP#1664). File-backed blobs can become
        // unreadable after the file input resets / picker closes; encoding
        // once here feeds both IndexedDB persistence and later preview/review
        // without re-reading a possibly-evicted File.
        setErrorMsg('Preparing photo…')
        const mimeType = file.type || 'image/jpeg'
        const dataUrl = await encodeBlobForStorage(blob, mimeType)
        const durableBlob = dataUrlToBlob(dataUrl)

        // GPS is best-effort — race with a 1s timeout so it never blocks
        const pos = await Promise.race([
          getPosition().catch(() => null),
          new Promise<null>((r) => setTimeout(() => r(null), 1000)),
        ])

        const photo: PendingPhoto = {
          photo_id: crypto.randomUUID(),
          survey_id: sid,
          visit_id: vid,
          area: a,
          caption: null,
          blob: durableBlob,
          mime_type: mimeType,
          gps_lat: pos?.lat ?? null,
          gps_lng: pos?.lng ?? null,
          taken_at: new Date().toISOString(),
        }

        setErrorMsg('Saving locally…')
        await surveyStore.savePhoto(photo, { dataUrl })

        if (mustUploadFirst) {
          setErrorMsg('Uploading evidence…')
          await uploadPendingPhotoEvidence(vid, photo)
          cb?.(photo)
        } else {
          cb?.(photo)
          setErrorMsg('Queuing sync…')
          await enqueuePhoto(vid, sid, photo.photo_id)
        }

        setStatus('saved')
        setErrorMsg(null)
        setTimeout(() => setStatus('idle'), 3000)
      } catch (err) {
        console.error('[PhotoCapture] Failed:', err)
        setStatus('error')
        setErrorMsg(
          err instanceof Error ? err.message : 'Photo capture failed — please try again',
        )
      }

      // Reset input so same file can be re-selected
      input.value = ''
    }

    input.addEventListener('change', handleChange)
    return () => input.removeEventListener('change', handleChange)
  }, []) // empty deps — the handler reads props via ref

  return (
    <div className="space-y-1">
      {/* The file input is full-size and overlays the styled trigger so the
          user's tap hits the real <input> directly. Programmatic click() and
          hidden inputs both break on iOS WebKit. Visual treatment is driven
          by `variant` so the guided survey can match the DailyAssessment
          look without forking the surveyStore plumbing. */}
      {variant === 'dashed-tile' ? (
        <div className="relative flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 p-4 text-sm font-bold text-slate-500 transition-colors hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]">
          <Camera size={18} />
          {label ?? 'Take Photo'}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      ) : variant === 'secondary' ? (
        <div className="relative inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500 hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)]">
          <Camera size={14} />
          {label ?? 'Add another'}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      ) : (
        <div className="relative inline-flex items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-[var(--brand-deep)] active:scale-[0.98]">
          <Camera size={16} />
          {label ?? 'Photo'}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      )}

      {status === 'processing' && (
        <p className="flex items-center gap-1 text-xs font-semibold text-blue-600">
          <Loader size={12} className="animate-spin" /> {errorMsg || 'Processing photo…'}
        </p>
      )}

      {status === 'saved' && (
        <p className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
          <CheckCircle size={12} /> {requireDurableUpload ? 'Photo captured and synced' : 'Photo captured — uploading in background'}
        </p>
      )}

      {status === 'error' && (
        <p className="flex items-center gap-1 text-xs font-semibold text-red-600">
          <AlertCircle size={12} /> {errorMsg || 'Photo capture failed'}
        </p>
      )}
    </div>
  )
}
